import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Trash2, Search, RotateCcw, ChevronLeft, ChevronRight, Loader2, Filter, X,
  ShieldAlert, AlertTriangle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { minDelay } from "@/lib/ux";
import { ErrorView, EmptyState } from "@/components/StateViews";
import { TableSkeleton } from "@/components/Skeletons";
import ConfirmDialog from "@/components/ConfirmDialog";
import DeletionLog from "@/components/DeletionLog";
import { QualityChip, StageChip } from "@/components/Chips";
import { fmtDateTime } from "@/lib/format";
import DateRangePicker from "@/components/DateRangePicker";
import PageSizeSelector from "@/components/PageSizeSelector";

const HARD_REASONS = new Set([
  "Test lead", "Production testing cleanup", "Wrong import", "Duplicate lead", "Spam/Junk lead",
]);

const sel = "h-9 rounded-xl border border-sky bg-white px-2.5 text-sm text-navy outline-none focus:border-loop focus:ring-2 focus:ring-loop/20";

function PermanentDeleteDialog({ open, onOpenChange, mode, count, reasons, onConfirm }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const isHard = HARD_REASONS.has(reason);
  const needsDelete = mode !== "selected" || isHard;
  const valid = reason && (reason !== "Other" || note.trim()) && (!needsDelete || confirmText.trim() === "DELETE");

  const headline = {
    selected: `You are about to permanently delete ${count} selected lead(s). This action cannot be undone.`,
    filtered: `You are about to permanently delete all ${count} lead(s) matching the current filters. Leads outside the filter are NOT affected. This cannot be undone.`,
    all: `You are about to permanently delete every lead in the Recycle Bin (${count}). This action cannot be undone.`,
  }[mode];

  const submit = async () => {
    setBusy(true);
    try { await onConfirm({ reason, note, confirm_text: confirmText }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && !v && onOpenChange(false)}>
      <DialogContent className="border-sky bg-white sm:max-w-lg" data-testid="permanent-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy">
            <ShieldAlert className="h-5 w-5 text-red-500" />
            {mode === "filtered" ? "Empty Filtered Results" : mode === "all" ? "Empty Entire Bin" : "Permanently delete"}
          </DialogTitle>
          <DialogDescription className="text-ink/70">{headline}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink/60">Delete reason <span className="text-red-500">*</span></label>
            <select data-testid="pd-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={`${sel} w-full`}>
              <option value="">Select a reason…</option>
              {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {reason === "Other" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-ink/60">Note <span className="text-red-500">*</span></label>
              <input data-testid="pd-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short note for audit…" className={`${sel} w-full`} />
            </div>
          )}
          {(isHard || mode !== "selected") && (
            <div className="flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="pd-hard-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>This will <strong>permanently delete</strong> the lead(s) and all related data — activities, SLA logs, follow-ups, notes, and reporting contribution. This cannot be undone.</span>
            </div>
          )}
          {needsDelete && (
            <div>
              <label className="mb-1 block text-xs font-medium text-ink/60">Type <strong className="text-red-600">DELETE</strong> to confirm</label>
              <input data-testid="pd-confirm-text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className={`${sel} w-full`} />
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button onClick={() => onOpenChange(false)} disabled={busy} data-testid="pd-cancel" className="flex-1 rounded-xl border border-sky py-2.5 text-sm font-medium text-ink/70 hover:bg-mist disabled:opacity-50">Cancel</button>
            <button onClick={submit} disabled={!valid || busy} data-testid="pd-confirm" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-500 py-2.5 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm delete"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_FILTERS = {
  search: "", deletedPreset: "", deletedFrom: "", deletedTo: "", createdPreset: "", createdFrom: "", createdTo: "",
  deleted_by: "", owner_user_id: "", source: "", stage: "", quality: "", reason: "", has_activity: "", has_deal: "",
};

export default function RecycleBin() {
  const [view, setView] = useState("bin");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [options, setOptions] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState(null); // {ids}
  const [pdDialog, setPdDialog] = useState(null); // { mode, count }
  const debounceRef = useRef();

  // Build the backend filter params (only non-empty), shared by list + empty actions.
  const apiFilters = useMemo(() => {
    const f = {};
    if (filters.search.trim()) f.search = filters.search.trim();
    if (filters.deletedFrom) f.deleted_from = filters.deletedFrom;
    if (filters.deletedTo) f.deleted_to = filters.deletedTo + "T23:59:59";
    if (filters.createdFrom) f.created_from = filters.createdFrom;
    if (filters.createdTo) f.created_to = filters.createdTo + "T23:59:59";
    ["deleted_by", "owner_user_id", "source", "stage", "quality", "reason", "has_activity", "has_deal"].forEach((k) => {
      if (filters[k]) f[k] = filters[k];
    });
    return f;
  }, [filters]);

  const filtersActive = Object.keys(apiFilters).length > 0;

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await minDelay(api.get("/leads/deleted", { params: { page, page_size: pageSize, ...apiFilters } }));
      setData(data.data);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, [page, pageSize, apiFilters]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, filters.search ? 350 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [load, filters.search]);

  useEffect(() => { api.get("/leads/deleted/filter-options").then(({ data }) => setOptions(data.data)).catch(() => {}); }, []);

  const togglePick = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allPicked = !!data?.items.length && data.items.every((l) => picked.has(l.id));
  const toggleAll = () => setPicked(() => allPicked ? new Set() : new Set((data?.items || []).map((l) => l.id)));
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setPage(1); setPicked(new Set()); };
  const setF = (k, v) => { setFilters((p) => ({ ...p, [k]: v })); setPage(1); setPicked(new Set()); };
  const goPage = (p) => { setPage(p); setPicked(new Set()); };

  const doRestore = async (ids) => {
    setBusy(true);
    try {
      if (ids.length === 1) await api.post(`/leads/${ids[0]}/restore`);
      else await api.post("/leads/bulk/restore", { lead_ids: ids });
      toast.success(`${ids.length} lead(s) restored`);
      setPicked(new Set()); setRestoreConfirm(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const doPermanentDelete = async ({ reason, note, confirm_text }) => {
    const mode = pdDialog.mode;
    const body = { reason, note, confirm_text };
    if (mode === "selected") body.lead_ids = Array.from(picked);
    else { body.apply_to_filtered = true; body.filters = mode === "filtered" ? apiFilters : {}; }
    try {
      const { data } = await api.post("/leads/permanent-delete", body);
      const { hard_deleted, anonymized, total } = data.data;
      toast.success(`${total} lead(s) permanently removed (${hard_deleted} hard-deleted, ${anonymized} anonymized)`);
      setPicked(new Set()); setPdDialog(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const emptyMode = filtersActive ? "filtered" : "all";
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const activeCount = Object.keys(apiFilters).length;

  return (
    <div className="space-y-4" data-testid="recycle-bin-page">
      <div>
        <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><Trash2 className="h-7 w-7 text-red-400" />Recycle Bin</h1>
        <p className="text-sm text-ink/60">{data ? `${data.total} deleted lead(s)` : "Loading…"} · restore, or permanently clean up.</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-sky/50">
        <div className="flex gap-1">
          {[{ k: "bin", label: "Recycle Bin" }, { k: "log", label: "Deletion Log" }].map((t) => (
            <button key={t.k} data-testid={`recycle-tab-${t.k}`} onClick={() => setView(t.k)} className={`-mb-px border-b-2 px-3 pb-2.5 pt-1 text-sm font-semibold transition-colors ${view === t.k ? "border-loop text-loop" : "border-transparent text-ink/50 hover:text-navy"}`}>{t.label}</button>
          ))}
        </div>
        {view === "bin" && (
        <div className="flex flex-wrap items-center gap-2 pb-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input data-testid="recycle-search" value={filters.search} onChange={(e) => setF("search", e.target.value)} placeholder="Search deleted leads…" className="h-10 w-60 rounded-xl border border-sky bg-white pl-9 pr-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
          </div>
          <button onClick={() => setShowFilters((s) => !s)} data-testid="recycle-toggle-filters" className={`flex h-10 items-center gap-1.5 rounded-xl border px-3 text-sm font-medium ${showFilters || activeCount ? "border-loop bg-loop/5 text-loop" : "border-sky bg-white text-ink/70"}`}>
            <Filter className="h-4 w-4" />Filters{activeCount > 0 && <span className="rounded-full bg-loop px-1.5 text-[11px] font-bold text-white">{activeCount}</span>}
          </button>
          <button onClick={() => setPdDialog({ mode: emptyMode, count: data?.total || 0 })} disabled={!data?.total} data-testid="recycle-empty-bin" className="flex h-10 items-center gap-1.5 rounded-xl bg-red-500 px-3 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-40">
            <Trash2 className="h-4 w-4" />{filtersActive ? "Empty Filtered Results" : "Empty Entire Bin"}
          </button>
        </div>
        )}
      </div>

      {view === "log" ? <DeletionLog /> : (
      <>
      {showFilters && (
        <div className="grid grid-cols-2 gap-3 rounded-2xl border border-sky/70 bg-white p-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="recycle-filters">
          <div className="flex flex-col gap-1 text-xs text-ink/55">Deleted date
            <DateRangePicker from={filters.deletedFrom} to={filters.deletedTo} onChange={({ created_from, created_to }) => setFilters((p) => ({ ...p, deletedFrom: created_from, deletedTo: created_to }))} testid="recycle-deleted-range" />
          </div>
          <label className="flex flex-col gap-1 text-xs text-ink/55">Deleted by
            <select data-testid="filter-deleted-by" value={filters.deleted_by} onChange={(e) => setF("deleted_by", e.target.value)} className={sel}><option value="">Anyone</option>{(options?.deleted_by || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink/55">Owner
            <select data-testid="filter-owner" value={filters.owner_user_id} onChange={(e) => setF("owner_user_id", e.target.value)} className={sel}><option value="">Anyone</option>{options?.has_unassigned && <option value="unassigned">Unassigned</option>}{(options?.owners || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink/55">Source
            <select data-testid="filter-source" value={filters.source} onChange={(e) => setF("source", e.target.value)} className={sel}><option value="">All sources</option>{(options?.sources || []).map((s) => <option key={s} value={s}>{s}</option>)}</select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink/55">Stage before delete
            <select data-testid="filter-stage" value={filters.stage} onChange={(e) => setF("stage", e.target.value)} className={sel}><option value="">All stages</option>{(options?.stages || []).map((s) => <option key={s} value={s}>{options?.stage_meta?.[s]?.name || s}</option>)}</select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink/55">Quality
            <select data-testid="filter-quality" value={filters.quality} onChange={(e) => setF("quality", e.target.value)} className={sel}><option value="">All</option>{(options?.qualities || []).map((s) => <option key={s} value={s}>{s}</option>)}</select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink/55">Delete reason
            <select data-testid="filter-reason" value={filters.reason} onChange={(e) => setF("reason", e.target.value)} className={sel}><option value="">Any reason</option>{(options?.reasons || []).map((s) => <option key={s} value={s}>{s}</option>)}<option value="__none__">Reason not available</option></select>
          </label>
          <div className="flex flex-col gap-1 text-xs text-ink/55">Created date
            <DateRangePicker from={filters.createdFrom} to={filters.createdTo} onChange={({ created_from, created_to }) => setFilters((p) => ({ ...p, createdFrom: created_from, createdTo: created_to }))} testid="recycle-created-range" />
          </div>
          <label className="flex flex-col gap-1 text-xs text-ink/55">Has activity
            <select data-testid="filter-has-activity" value={filters.has_activity} onChange={(e) => setF("has_activity", e.target.value)} className={sel}><option value="">Any</option><option value="yes">Has activity</option><option value="no">No activity</option></select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink/55">Deal / payment
            <select data-testid="filter-has-deal" value={filters.has_deal} onChange={(e) => setF("has_deal", e.target.value)} className={sel}><option value="">Any</option><option value="yes">Has deal/payment</option><option value="no">No deal/payment</option></select>
          </label>
          <div className="col-span-full flex justify-end">
            <button onClick={clearFilters} disabled={!activeCount} data-testid="recycle-clear-filters" className="flex items-center gap-1 rounded-lg border border-sky px-3 py-1.5 text-xs font-medium text-ink/60 hover:bg-mist disabled:opacity-40"><X className="h-3.5 w-3.5" />Clear filters</button>
          </div>
        </div>
      )}

      {picked.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sky bg-white px-4 py-2.5" data-testid="recycle-bulk-bar">
          <span className="text-sm font-semibold text-navy">{picked.size} selected (on this page)</span>
          <div className="flex gap-2">
            <button onClick={() => setRestoreConfirm({ ids: Array.from(picked) })} disabled={busy} data-testid="recycle-bulk-restore" className="flex items-center gap-1.5 rounded-lg bg-loop px-3 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60"><RotateCcw className="h-4 w-4" />Restore</button>
            <button onClick={() => setPdDialog({ mode: "selected", count: picked.size })} disabled={busy} data-testid="recycle-bulk-permanent-delete" className="flex items-center gap-1.5 rounded-lg bg-red-500 px-3 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-60"><Trash2 className="h-4 w-4" />Delete permanently</button>
          </div>
        </div>
      )}

      {loading ? <TableSkeleton rows={6} cols={7} /> : error ? <ErrorView message="Couldn't load the recycle bin." onRetry={load} /> :
        !data?.items.length ? (
          <EmptyState title={filtersActive ? "No leads match these filters" : "Recycle bin is empty"} subtitle="Deleted leads appear here and can be restored anytime." />
        ) : (
          <>
            <div className="overflow-hidden rounded-2xl border border-sky/70 bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-mist text-left text-xs font-semibold uppercase tracking-wide text-ink/50">
                    <tr>
                      <th className="w-10 px-4 py-3"><input type="checkbox" data-testid="recycle-select-all" checked={allPicked} onChange={toggleAll} className="h-4 w-4 rounded border-sky text-loop" /></th>
                      {["Lead", "Quality", "Stage", "Source", "Deleted by", "Deleted at", "Reason", ""].map((h) => <th key={h} className="whitespace-nowrap px-4 py-3">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-sky/50">
                    {data.items.map((l) => (
                      <tr key={l.id} data-testid={`recycle-row-${l.id}`} className={picked.has(l.id) ? "bg-loop/5" : ""}>
                        <td className="px-4 py-3"><input type="checkbox" data-testid={`recycle-check-${l.id}`} checked={picked.has(l.id)} onChange={() => togglePick(l.id)} className="h-4 w-4 rounded border-sky text-loop" /></td>
                        <td className="px-4 py-3"><p className="font-medium text-navy">{l.name || "—"}</p><p className="text-xs text-ink/50">{l.business_name || l.email || l.phone || "—"}{l.has_deal && <span className="ml-1 rounded bg-emerald-50 px-1 text-[10px] font-semibold text-emerald-600">deal</span>}</p></td>
                        <td className="px-4 py-3"><QualityChip value={l.quality} /></td>
                        <td className="px-4 py-3"><StageChip value={l.stage_before_delete || l.stage} /></td>
                        <td className="whitespace-nowrap px-4 py-3 text-xs text-ink/60">{l.ingestion_source || "—"}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-ink/70">{l.deleted_by_name || "—"}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-ink/60">{fmtDateTime(l.deleted_at)}</td>
                        <td className="px-4 py-3 text-xs text-ink/50">{l.deleted_reason || "Reason not available"}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => setRestoreConfirm({ ids: [l.id] })} disabled={busy} data-testid={`recycle-restore-${l.id}`} className="inline-flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-semibold text-loop hover:bg-sky/30 disabled:opacity-60"><RotateCcw className="h-3.5 w-3.5" />Restore</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm text-ink/50">Page {data.page} of {totalPages}</p>
              <div className="flex items-center gap-3">
                <PageSizeSelector value={pageSize} onChange={(n) => { setPageSize(n); goPage(1); }} testid="recycle-page-size" />
                <div className="flex gap-2">
                  <button disabled={page <= 1} onClick={() => goPage(page - 1)} data-testid="recycle-prev" className="flex items-center gap-1 rounded-lg border border-sky bg-white px-3 py-2 text-sm font-medium text-navy disabled:opacity-40"><ChevronLeft className="h-4 w-4" />Prev</button>
                  <button disabled={page >= totalPages} onClick={() => goPage(page + 1)} data-testid="recycle-next" className="flex items-center gap-1 rounded-lg border border-sky bg-white px-3 py-2 text-sm font-medium text-navy disabled:opacity-40">Next<ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          </>
        )}
      </>
      )}

      <ConfirmDialog
        open={!!restoreConfirm}
        onOpenChange={(v) => !v && setRestoreConfirm(null)}
        onConfirm={() => doRestore(restoreConfirm.ids)}
        title="Restore lead(s)?"
        description={restoreConfirm ? `${restoreConfirm.ids.length} lead(s) will return to the Leads list and Pipeline at their previous stage.` : ""}
        confirmLabel="Restore"
        destructive={false}
        testid="recycle-restore-confirm"
      />

      {pdDialog && (
        <PermanentDeleteDialog
          open={!!pdDialog}
          onOpenChange={() => setPdDialog(null)}
          mode={pdDialog.mode}
          count={pdDialog.count}
          reasons={options?.reasons || []}
          onConfirm={doPermanentDelete}
        />
      )}
    </div>
  );
}
